import type React from "react";
import { useMemo, useState } from "react";
import { importApi, mastersApi, settingsApi, type ImportReport } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad, errText } from "../ui/useLoad";
import { ErrorNote, Loading } from "../ui/bits";
import { download } from "../ui/download";
import { parseCsv } from "../lib/csv";
import { xlsx } from "../lib/xlsx";
import { readHtmlTable, readXlsx } from "../banking/bankCsv";
import { TEMPLATES, findHeader, guessMapping, rowsOf, templateHeader, toCanonical, type Ctx, type SheetKind } from "./sheetImport";

async function readTable(f: File): Promise<string[][]> {
  const buf = await f.arrayBuffer();
  const head = new TextDecoder().decode(buf.slice(0, 512)).trimStart();
  if (head.startsWith("PK")) return readXlsx(buf);
  if (/^<(!doctype|html|table|\?xml)/i.test(head)) return readHtmlTable(new TextDecoder().decode(buf));
  if (/\.xls$/i.test(f.name)) throw new Error("This is an old-style .xls file. Open it in Excel and save it as .xlsx or CSV.");
  return parseCsv(new TextDecoder().decode(buf).replace(/^﻿/, ""));
}

// Company › Import › Excel or CSV: pick what the sheet holds, match its columns, check, import
export function SheetImport({ onDone, Report }: { onDone: () => void; Report: (p: { r: ImportReport }) => React.ReactElement }) {
  const { company } = useCompany();
  const [kind, setKind] = useState<SheetKind>("parties");
  const [ptype, setPtype] = useState<"customer" | "supplier" | "both">("customer");
  const [table, setTable] = useState<string[][] | null>(null);
  const [fileName, setFileName] = useState("");
  const [header, setHeader] = useState(0);
  const [map, setMap] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const ref = useLoad(async () => {
    const [m, s, l] = await Promise.all([mastersApi.reference(company.id), settingsApi.get(company.id), mastersApi.ledgers(company.id)]);
    return { m, s, l };
  }, [company.id]);
  const t = TEMPLATES[kind];

  const pick = async (f?: File) => {
    if (!f) return;
    setErr(null); setReport(null); setMsg(null);
    try {
      const tb = await readTable(f);
      if (!tb.length) throw new Error("That file has no rows.");
      const h = findHeader(tb, t.fields);
      setTable(tb); setFileName(f.name); setHeader(h); setMap(guessMapping(tb[h], t.fields));
    } catch (e) { setErr(errText(e)); setTable(null); }
  };
  const changeKind = (k: SheetKind) => {
    setKind(k); setReport(null); setMsg(null);
    if (table) { const h = findHeader(table, TEMPLATES[k].fields); setHeader(h); setMap(guessMapping(table[h], TEMPLATES[k].fields)); }
  };

  const ctx: Ctx | null = useMemo(() => {
    if (!ref.data) return null;
    const { m, s, l } = ref.data;
    const regs = s.gst?.registrations ?? [];
    const base = regs.find((r) => r.id === s.gst?.settings?.default_registration) ?? regs[0];
    return { source: "excel", defaultPartyType: ptype, baseState: base?.state_code ?? null, states: m.gst_states ?? [],
      taxLedgers: (base?.tax_ledgers ?? []).map((x) => ({ ledger: x.ledger, head: x.head, direction: x.direction, rcm: x.rcm })),
      parties: l.filter((x) => x.is_party).map((x) => ({ name: x.name, state: x.gstin ? x.gstin.slice(0, 2) : null })),
      booksBegin: m.company.books_begin };
  }, [ref.data, ptype]);
  const conv = useMemo(() => (table && ctx ? toCanonical(kind, rowsOf(table, header, map), ctx) : null), [table, ctx, kind, header, map]);
  const missing = t.fields.filter((f) => f.required && !(map[f.key] >= 0));
  // what stops a check: a problem (not a note), or nothing found to bring in
  const blocking = conv ? conv.problems.filter((p) => !p.note) : [];
  const nothing = !!conv && /^0 /.test(conv.summary);

  const run = async (commit: boolean) => {
    if (!conv) return;
    setBusy(true); setErr(null); setMsg(null); if (!commit) setReport(null);
    try {
      if (kind === "openings") {
        const sheet = await mastersApi.openings(company.id);
        const byName = new Map(sheet.ledgers.map((x) => [x.name.trim().toLowerCase(), x.id]));
        const unknown = conv.openings!.filter((o) => !byName.has(o.ledger.trim().toLowerCase())).map((o) => o.ledger);
        if (unknown.length) throw new Error(`Not in the books: ${unknown.slice(0, 8).join(", ")}${unknown.length > 8 ? ` and ${unknown.length - 8} more` : ""}. Import the chart of accounts first, or fix the names.`);
        if (!commit) { setMsg(`Ready: ${conv.openings!.length} opening balances for ${sheet.year.label}. Existing openings of these ledgers are replaced.`); return; }
        const n = await mastersApi.setOpenings(company.id, conv.openings!.map((o) => ({ ledger_id: byName.get(o.ledger.trim().toLowerCase())!, side: o.side, amount: o.amount })));
        setMsg(`Opening balances saved for ${n} ledgers.`); onDone(); return;
      }
      const r = await importApi.run(company.id, "canonical", { data: conv.data }, {}, commit);
      setReport(r);
      if (commit) onDone();
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  if (!ref.data) return ref.error ? <ErrorNote text={ref.error.message} /> : <Loading />;
  const cols = table?.[header] ?? [];
  const preview = table ? rowsOf(table, header, map).slice(0, 5) : [];
  const shown = t.fields.filter((f) => map[f.key] >= 0 || f.required);
  return (
    <>
      <div className="fields inline">
        <label>The sheet holds<select value={kind} onChange={(e) => changeKind(e.target.value as SheetKind)}>
          {(Object.keys(TEMPLATES) as SheetKind[]).map((k) => <option key={k} value={k}>{TEMPLATES[k].title}</option>)}</select></label>
        {kind === "parties" && <label>Rows with no type are<select value={ptype} onChange={(e) => setPtype(e.target.value as typeof ptype)}>
          <option value="customer">Customers</option><option value="supplier">Vendors</option><option value="both">Both</option></select></label>}
        <button className="link" onClick={() => download(`Books import - ${t.title}.xlsx`, new Blob([xlsx(t.title, templateHeader(kind).map((label) => ({ label })), []) as Uint8Array<ArrayBuffer>],
          { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }))}>Download a blank template</button>
      </div>
      <p className="muted small">{t.about} Any column order and names work: match them below. Excel (.xlsx), CSV, or the HTML “.xls” many systems export.</p>
      <label className="file">Spreadsheet<input type="file" accept=".xlsx,.xls,.csv,text/csv" onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ""; }} /></label>
      {table && <>
        <p className="small">{fileName}: {table.length - header - 1} rows below the header (row {header + 1}).{" "}
          <label className="inline-label">Header row <input type="number" min={1} max={Math.min(table.length, 20)} value={header + 1} style={{ width: 64 }}
            onChange={(e) => { const h = Math.max(0, Number(e.target.value) - 1); setHeader(h); setMap(guessMapping(table[h] ?? [], t.fields)); }} /></label></p>
        <table className="grid-table compact"><thead><tr><th>Books field</th><th>Column in the sheet</th><th className="muted">First value</th></tr></thead>
          <tbody>{t.fields.map((f) => <tr key={f.key}>
            <td>{f.label}{f.required ? " *" : ""}{f.hint && <div className="muted small">{f.hint}</div>}</td>
            <td><select value={map[f.key] ?? -1} onChange={(e) => setMap({ ...map, [f.key]: Number(e.target.value) })}>
              <option value={-1}>— not in the sheet —</option>{cols.map((c, i) => <option key={i} value={i}>{c || `Column ${i + 1}`}</option>)}</select></td>
            <td className="muted small">{preview[0]?.v[f.key] ?? ""}</td></tr>)}</tbody></table>
        {missing.length > 0 && <p className="warn small">Match a column for: {missing.map((f) => f.label).join(", ")}.</p>}
        {missing.length === 0 && preview.length > 0 && <>
          <h3>First rows as Books reads them</h3>
          <div className="table-wrap"><table className="grid-table compact"><thead><tr><th>Row</th>{shown.map((f) => <th key={f.key}>{f.label}</th>)}</tr></thead>
            <tbody>{preview.map((r) => <tr key={r.row}><td className="muted">{r.row}</td>{shown.map((f) => <td key={f.key}>{r.v[f.key]}</td>)}</tr>)}</tbody></table></div>
        </>}
        {conv && missing.length === 0 && <>
          <p className="small">Found: {conv.summary}.</p>
          {blocking.length > 0 && <ul className="msg err errors">{blocking.slice(0, 30).map((p, i) => <li key={i}>{p.row ? `Row ${p.row}: ` : ""}{p.message}</li>)}
            {blocking.length > 30 && <li>… and {blocking.length - 30} more</li>}</ul>}
          {conv.problems.some((p) => p.note) && <ul className="small warn">{conv.problems.filter((p) => p.note).map((p, i) => <li key={i}>{p.message}</li>)}</ul>}
          {nothing && blocking.length === 0 && <p className="warn small">Nothing to bring in: no row has what Books needs. Check the columns matched above.</p>}
          {blocking.length > 0 && <p className="small muted">Fix {blocking.length === 1 ? "this" : "these"} in the sheet and choose the file again; nothing has been kept.</p>}
          <div className="row">
            <button className="primary" disabled={busy || blocking.length > 0 || nothing} onClick={() => void run(false)}>{busy ? "Working…" : "Check"}</button>
            {((report && report.problems.length === 0 && !report.committed) || (kind === "openings" && msg?.startsWith("Ready"))) &&
              <button className="primary" disabled={busy} onClick={() => void run(true)}>Import now</button>}
          </div>
        </>}
      </>}
      {msg && <p className="msg ok">{msg}</p>}
      <ErrorNote text={err} />
      {report && <Report r={report} />}
    </>
  );
}
